import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { KidAvatar } from "../Brand";
import { parentApi, errMsg } from "../../lib/api";
import { AVATARS, COLORS } from "../../lib/constants";

const blank = { name: "", age: 8, avatar: "cat", color: COLORS[0], pin: "" };

const KidForm = ({ initial, onSubmit, busy, isEdit }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-4">
      <div className="flex justify-center"><KidAvatar avatar={f.avatar} color={f.color} size={88} /></div>
      <input data-testid="kid-form-name" className="zn-input" placeholder="Nickname (not full name)" value={f.name} onChange={(e) => set("name", e.target.value)} required maxLength={30} />
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="kid-form-age" type="number" min={3} max={17} className="zn-input" placeholder="Age" value={f.age} onChange={(e) => set("age", Number(e.target.value))} required />
        <input data-testid="kid-form-pin" className="zn-input tracking-[0.5em]" inputMode="numeric" maxLength={4} pattern="\d{4}" placeholder={isEdit ? "New PIN" : "PIN"} value={f.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, ""))} required={!isEdit} />
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.keys(AVATARS).map((a) => (
          <button type="button" key={a} data-testid={`kid-form-avatar-${a}`} onClick={() => set("avatar", a)} className={`rounded-full ${f.avatar === a ? "ring-4 ring-[#7B5CFA]" : ""}`}>
            <KidAvatar avatar={a} color="#fff" size={40} />
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        {COLORS.map((c) => (
          <button type="button" key={c} data-testid={`kid-form-color-${c.slice(1)}`} onClick={() => set("color", c)} className={`w-9 h-9 rounded-full border-[3px] border-[#1B1A2E] ${f.color === c ? "ring-4 ring-[#7B5CFA]" : ""}`} style={{ background: c }} />
        ))}
      </div>
      <button data-testid="kid-form-submit" disabled={busy} className="zn-btn bg-[#FF6B4A] text-white w-full">{isEdit ? "Save changes" : "Create profile"}</button>
    </form>
  );
};

export const KidManager = ({ kids, onChange }) => {
  const [editing, setEditing] = useState(null); // null | "new" | kid
  const [busy, setBusy] = useState(false);

  const save = async (f) => {
    setBusy(true);
    try {
      const body = { name: f.name, age: f.age, avatar: f.avatar, color: f.color, ...(f.pin ? { pin: f.pin } : {}) };
      if (editing === "new") await parentApi.post("/kids", body);
      else await parentApi.patch(`/kids/${editing.id}`, body);
      toast.success("Profile saved");
      setEditing(null);
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Delete ${k.name}'s profile and all their videos?`)) return;
    await parentApi.delete(`/kids/${k.id}`);
    toast.success("Profile deleted");
    onChange();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold">Kid profiles</h1>
        <button data-testid="add-kid-btn" onClick={() => setEditing("new")} className="zn-btn bg-[#FFD23F]"><Plus size={18} /> Add kid</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {kids.map((k) => (
          <div key={k.id} data-testid={`kid-card-${k.id}`} className="zn-card p-6 bg-white flex flex-col items-center text-center gap-2">
            <KidAvatar avatar={k.avatar} color={k.color} frame={k.frame} size={80} />
            <h3 className="font-display text-2xl font-semibold">{k.name}</h3>
            <p className="text-sm text-[#1B1A2E]/60">Age {k.age} · {k.settings.daily_limit_minutes} min/day</p>
            <div className="flex gap-2 mt-2">
              <button data-testid={`edit-kid-${k.id}`} onClick={() => setEditing({ ...k, pin: "" })} className="zn-btn bg-white !py-2 !px-4"><Pencil size={16} /> Edit</button>
              <button data-testid={`delete-kid-${k.id}`} onClick={() => remove(k)} className="zn-btn bg-white !py-2 !px-4"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="kid-form-dialog" className="border-[3px] border-[#1B1A2E] rounded-3xl shadow-[6px_6px_0_0_#1B1A2E] max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "new" ? "New kid profile" : `Edit ${editing?.name}`}</DialogTitle></DialogHeader>
          {editing && <KidForm initial={editing === "new" ? blank : editing} onSubmit={save} busy={busy} isEdit={editing !== "new"} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
